/**
 * SiteRouter — niche dispatcher for /r/:slug/*
 *
 * Fetches the entity once and renders either MedicalSite (for niche="Medical")
 * or RestaurantSite (default). Avoids duplicating routing logic and lets the
 * same /r/:slug URL work for every directory entity.
 *
 * iter373c patch F (2026-09-22) — Accepts an optional `forceSlug` prop so
 * whitelabel hosts (e.g. `taxassistoxley.au` mapped in `hostRegistry.js`)
 * can render the vendor site inline at the root path `/` without the
 * `/r/:slug` URL segment showing in the browser bar. The slug is also
 * published on `WhitelabelSlugContext` so nested layout components can
 * pick it up as a fallback when `useParams().slug` is undefined.
 */
import { createContext, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import MedicalSite from "@/pages/MedicalSite";
import RestaurantSite from "@/pages/RestaurantSite";

// Exported so RestaurantSite's SiteLayout + MedicalSite's MedicalSiteLayout
// can read `slug` as a fallback when the browser URL doesn't carry `/r/:slug`.
export const WhitelabelSlugContext = createContext(null);
export function useWhitelabelSlug() {
  return useContext(WhitelabelSlugContext);
}

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
export default function SiteRouter({ forceSlug } = {}) {
  const params = useParams();
  const slug = forceSlug || params.slug;
  const [niche, setNiche] = useState(undefined); // undefined = loading

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/directory/restaurants/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { if (!cancelled) setNiche(d.niche || "Restaurants"); })
      .catch(() => { if (!cancelled) setNiche("Restaurants"); });
    return () => { cancelled = true; };
  }, [slug]);

  if (niche === undefined) {
    return (
      <div data-testid="site-router-loading" className="min-h-[60vh] grid place-items-center text-gray-500">
        Loading…
      </div>
    );
  }
  const inner = niche === "Medical" ? <MedicalSite /> : <RestaurantSite />;
  // Publish the slug on context so the inner <Routes> layouts can read it
  // even when the browser URL is bare `/` (whitelabel host mode).
  return (
    <WhitelabelSlugContext.Provider value={forceSlug || null}>
      {inner}
    </WhitelabelSlugContext.Provider>
  );
}
